import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  AccountTree as GraphIcon,
  Map as MapIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React, { createContext, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { ACCENT_COLOR, monospaceStyle } from "../../../theme";
import {
  EntityTypeActions,
  getEntityTypeEntry,
} from "../../../types/entityRegistry";
import { MainEntity } from "../../../types/entityTypes";
import { entityPath } from "../../../utils/entityAddress";
import ExportMenu, { ExportOption } from "../../common/ExportMenu";
import AddToPortfolioButton from "../../Portfolio/AddToPortfolioButton";
import AddToTrackButton from "../../ResearchWorkflow/AddToTrackButton";

interface EntityFunctionalHeaderProps {
  mainEntity: MainEntity | null;
  onClose: () => void;
  mode?: "overlay" | "standalone";
  // Further buttons of the view, shown first in the row of navigation
  // buttons, e.g. a kind's "make root"
  extraActions?: React.ReactNode;
  // The ways to export the entity; the export menu is shown when given
  exportOptions?: ExportOption[];
}

// The extra actions of a view built on another view, which renders the
// header: e.g. the class view's "make root" over the category view. Shown
// where the header is given no extra actions of its own.
export const HeaderExtraActionsContext = createContext<React.ReactNode>(null);

// Height (px) of the header bar
const HEADER_HEIGHT = 52;

// The accessible name of the block that holds the type chip and the id
export const ENTITY_TYPE_AND_ID = "Entity type and id";

// A square icon button of the header, with the corners of a surface
const headerButtonSx = {
  width: 36,
  height: 36,
  borderRadius: 2,
  "& .MuiSvgIcon-root": { fontSize: 20 },
} as const;

// A navigation button of the header: quiet until pointed at
const navButtonSx = {
  ...headerButtonSx,
  color: "text.secondary",
  "&:hover": { backgroundColor: "action.hover" },
} as const;

// An action button of the header, in the primary colour
const actionButtonSx = {
  ...headerButtonSx,
  color: "primary.main",
  "&:hover": { backgroundColor: "primary.main", color: "white" },
} as const;

// The type chip: small, outlined in the accent, written in the link colour
const typeChipSx = (theme: Theme) => {
  // Rendered without the app theme, e.g. in a test, the palette has no accent
  const accent = theme.palette.accent?.main ?? ACCENT_COLOR;
  return {
    flexShrink: 0,
    height: 20,
    fontSize: "0.71875rem",
    color: "link.main",
    borderColor: alpha(accent, 0.45),
    backgroundColor: alpha(accent, 0.1),
  };
};

// The room (px) the title leaves per centred action button, so that a long
// id stops short of the buttons
const ROOM_PER_ACTION = 20;

// The name of a type the registry does not know, e.g. "Spaceship"
const unregisteredTypeLabel = (entityType: string | undefined): string =>
  entityType
    ? entityType.charAt(0).toUpperCase() + entityType.slice(1)
    : "Entity";

// The actions shown while the type is not one the registry knows
const UNREGISTERED_TYPE_ACTIONS: EntityTypeActions = {
  map: true,
  graph: false,
  portfolio: true,
  track: false,
};

/**
 * Functional header with navigation and action buttons
 * Always spans full width
 */
const EntityFunctionalHeader: React.FC<EntityFunctionalHeaderProps> = ({
  mainEntity,
  onClose,
  mode = "overlay",
  extraActions,
  exportOptions,
}) => {
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  const inheritedActions = useContext(HeaderExtraActionsContext);
  const typeEntry = getEntityTypeEntry(mainEntity?.entityType);
  const actions = typeEntry?.actions ?? UNREGISTERED_TYPE_ACTIONS;
  const typeLabel =
    typeEntry?.label ?? unregisteredTypeLabel(mainEntity?.entityType);

  // The centred action buttons shown, in the order the features are asked for
  const showMap = actions.map && hasFeature("map");
  const showGraph = Boolean(mainEntity) && actions.graph && hasFeature("graph");
  const showPortfolio =
    Boolean(mainEntity) && actions.portfolio && hasFeature("portfolios");
  const showTrack =
    Boolean(mainEntity) && actions.track && hasFeature("research");
  const centeredActionCount = [
    showMap,
    showGraph,
    showPortfolio,
    showTrack,
  ].filter(Boolean).length;

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleForward = useCallback(() => {
    window.history.forward();
  }, []);

  const handleOpenInGraph = useCallback(
    (entityId: string, openInNewTab?: boolean) => {
      const graphParams = new URLSearchParams();
      graphParams.set("seed_entity", entityId);
      graphParams.set("org_structure", "parents_subsidiaries");
      if (openInNewTab) {
        window.open(`/graph?${graphParams.toString()}`, "_blank");
      } else {
        navigate(`/graph?${graphParams.toString()}`);
      }
    },
    [navigate],
  );

  const handleOpenInMap = (
    entityName: string,
    entityType: string,
    entityId: string,
    openInNewTab?: boolean,
  ) => {
    let url;
    if (entityType === "organization") {
      const facilityOwnerData = JSON.stringify([
        { id: entityId, name: entityName },
      ]);
      url = `/map?facilityOwnerCompanies=${encodeURIComponent(facilityOwnerData)}&facilityOwnerMaxTiers=1`;
    } else {
      url = `/map?search=${encodeURIComponent(entityName)}`;
    }

    if (openInNewTab) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleOpenInFullView = useCallback(() => {
    if (mainEntity) {
      navigate(entityPath(mainEntity.id, mainEntity.entityType));
    }
  }, [mainEntity, navigate]);

  const handleOpenInNewTab = useCallback(() => {
    if (mainEntity) {
      window.open(entityPath(mainEntity.id, mainEntity.entityType), "_blank");
    }
  }, [mainEntity]);

  return (
    <Box
      sx={{
        height: HEADER_HEIGHT,
        pl: 2.5,
        pr: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
      }}
    >
      {/* Left: the type as a chip, and the entity's id */}
      <Box
        role="group"
        aria-label={ENTITY_TYPE_AND_ID}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flex: 1,
          minWidth: 0,
          // Stops a long id short of the centred action buttons
          maxWidth:
            centeredActionCount > 0
              ? `calc(50% - ${centeredActionCount * ROOM_PER_ACTION + 8}px)`
              : undefined,
        }}
      >
        <Chip
          label={typeLabel}
          size="small"
          variant="outlined"
          sx={typeChipSx}
        />
        {mainEntity && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={mainEntity.id}
            sx={monospaceStyle}
          >
            {mainEntity.id}
          </Typography>
        )}
      </Box>

      {/* Center: Action buttons */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {/* Map button - hidden for projects and users without map access */}
        {showMap && (
          <IconButton
            onClick={(event) => {
              if (mainEntity && handleOpenInMap) {
                handleOpenInMap(
                  mainEntity.name,
                  mainEntity.entityType,
                  mainEntity.id,
                  false,
                );
              }
            }}
            onMouseDown={(event) => {
              if (event.button === 1 && mainEntity && handleOpenInMap) {
                event.preventDefault();
                handleOpenInMap(
                  mainEntity.name,
                  mainEntity.entityType,
                  mainEntity.id,
                  true,
                );
              }
            }}
            size="small"
            sx={actionButtonSx}
            title="Open in Map View"
          >
            <MapIcon />
          </IconButton>
        )}

        {/* Open in Graph View button - only for organizations with graph access */}
        {mainEntity &&
          showGraph &&
          handleOpenInGraph && (
          <IconButton
            onClick={() => handleOpenInGraph(mainEntity.id)}
            onMouseDown={(event) => {
              if (event.button === 1) {
                event.preventDefault();
                handleOpenInGraph(mainEntity.id, true);
              }
            }}
            size="small"
            sx={actionButtonSx}
            title="Open in Graph View (Middle-click to open in new tab)"
          >
            <GraphIcon />
          </IconButton>
        )}

        {/* Add to Portfolio button - visible for users with portfolio access */}
        {mainEntity && showPortfolio && (
          <AddToPortfolioButton
            entityType={
              mainEntity.entityType as
                | "organization"
                | "facility"
                | "project"
                | "source"
            }
            entityId={mainEntity.id}
            entityName={mainEntity.name}
            variant="icon"
            size="small"
            onSuccess={() => {}}
          />
        )}

        {/* Add to Research Track button - organizations, full research access only */}
        {mainEntity &&
          showTrack && (
          <AddToTrackButton
            entityType={
              mainEntity.entityType as "organization" | "facility" | "project"
            }
            entityId={mainEntity.id}
            entityName={mainEntity.name}
            variant="icon"
            size="small"
            onSuccess={() => {}}
          />
        )}
      </Box>

      {/* Right: Navigation buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          justifyContent: "flex-end",
          gap: 0.25,
        }}
      >
        {/* The view's own buttons, first */}
        {extraActions ?? inheritedActions}
        {exportOptions && exportOptions.length > 0 && (
          <Box sx={{ px: 0.75, fontSize: "0.8125rem" }}>
            <ExportMenu options={exportOptions} />
          </Box>
        )}
        {/* Open in New Tab button - only visible in overlay mode */}
        {mode === "overlay" && mainEntity && (
          <IconButton
            onClick={handleOpenInNewTab}
            size="small"
            sx={navButtonSx}
            title="Open in New Tab"
          >
            <OpenInNewIcon />
          </IconButton>
        )}
        {/* Open in Full View button - only visible in overlay mode */}
        {mode === "overlay" && mainEntity && (
          <IconButton
            onClick={handleOpenInFullView}
            size="small"
            sx={navButtonSx}
            title="Open in Full View"
          >
            <FullscreenIcon />
          </IconButton>
        )}
        <IconButton
          onClick={handleBack}
          size="small"
          title="Go Back"
          sx={navButtonSx}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton
          onClick={handleForward}
          size="small"
          title="Go Forward"
          sx={navButtonSx}
        >
          <ArrowForwardIcon />
        </IconButton>
        {/* Close button - only visible in overlay mode */}
        {mode === "overlay" && (
          <IconButton
            onClick={onClose}
            size="small"
            title="Close"
            sx={navButtonSx}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default EntityFunctionalHeader;
